import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Package, Truck, MapPin, ExternalLink } from "lucide-react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConditionBadge } from "@/components/ConditionBadge";

const STATUS_STYLE = {
  paid: "bg-success text-ink",
  shipped: "bg-link text-white",
  pending: "bg-surface2 text-ink",
};

export default function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user) api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  if (user === null) return <div className="py-24 text-center font-mono text-subtle">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-8 py-8" data-testid="my-orders-page">
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mb-6">MY ORDERS</h1>

      {orders === null ? (
        <div className="py-16 text-center font-mono text-subtle">Loading your orders…</div>
      ) : orders.length === 0 ? (
        <div className="border-2 border-dashed border-subtle p-12 text-center" data-testid="orders-empty">
          <Package className="h-8 w-8 mx-auto mb-3 text-subtle" />
          <p className="font-mono text-sm text-subtle">You haven't placed any orders yet.</p>
          <Link to="/" className="inline-block mt-4 bg-ink text-paper px-6 h-10 leading-10 font-mono text-xs uppercase tracking-wider hover:bg-link transition-colors">
            Browse Catalog
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border-2 border-ink bg-paper">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink bg-surface px-4 py-3">
                <div className="font-mono text-xs">
                  <span className="text-subtle uppercase tracking-wider">Order</span>{" "}
                  <strong>#{String(o.id).slice(0, 8)}</strong>
                  <span className="text-subtle"> · {(o.created_at || "").slice(0, 10)}</span>
                </div>
                <span className={`font-mono text-[11px] uppercase tracking-wider px-2 py-1 ${STATUS_STYLE[o.status] || "bg-surface2"}`}>
                  {o.status}
                </span>
              </div>

              <div className="px-4 py-3 space-y-2">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 font-mono text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate">{it.name}</span>
                      <ConditionBadge condition={it.condition} />
                    </div>
                    <span className="whitespace-nowrap text-subtle">×{it.quantity} · {money(it.line_total)}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink px-4 py-3">
                <div className="font-mono text-xs text-subtle flex items-center gap-1">
                  {o.fulfillment === "pickup" ? (
                    <><MapPin className="h-3.5 w-3.5" /> Local pickup</>
                  ) : o.tracking_number ? (
                    <a data-testid={`track-${o.id}`} href={`https://www.ups.com/track?tracknum=${o.tracking_number}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-link hover:underline">
                      <Truck className="h-3.5 w-3.5" /> Track {o.carrier || "UPS"} #{o.tracking_number} <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <><Truck className="h-3.5 w-3.5" /> Ships soon — tracking emailed on dispatch</>
                  )}
                </div>
                <div className="font-heading font-black text-lg tracking-tighter">{money(o.total)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
